// FormDatePicker.tsx

import React, { useRef } from 'react';
import { formatDateToInput, formatDateToDDMMYYYY, formatDateToMyanmar } from "../../utils/GlobalUtils";

interface FormDatePickerProps<T extends string = string> {
  name: T;
  value: string | null | undefined;
  isEditable?: boolean;
  useMyanmarDateFormat?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear?: (name: T) => void;
  placeholderText?: string;
  className?: string;
}

export const FormDatePicker = <T extends string>({
  name,
  value = '',
  isEditable = true,
  useMyanmarDateFormat = true,
  onChange,
  onClear,
  placeholderText = '(ရက်စွဲမရှိပါ)',
  className = '',
}: FormDatePickerProps<T>) => {
  const dateInputRef = useRef<HTMLInputElement>(null);
  const rawValue = value || '';

  // YYYY-MM-DD for standard HTML date input
  const inputDateValue = formatDateToInput(rawValue);

  // DD/MM/YYYY for visual display
  const displayDateValue = useMyanmarDateFormat
    ? formatDateToMyanmar(rawValue)
    : formatDateToDDMMYYYY(rawValue);

  // VIEW / PRINT MODE
  if (!isEditable) {
    return (
      <div className={`min-h-[2.25rem] flex items-center whitespace-nowrap border-0 border-b border-dashed border-slate-400 bg-transparent px-1 text-sm text-slate-800 font-medium ${className}`}>
        {displayDateValue || <span className="text-slate-300 italic font-normal">{placeholderText}</span>}
      </div>
    );
  }

  // EDIT MODE
  return (
    <div className={`relative flex min-w-28 items-center w-full ${className}`}>
      {/* Keep the native date input full-sized so its calendar picker can open reliably. */}
      <input
        ref={dateInputRef}
        type="date"
        lang="en-GB"
        name={name}
        value={inputDateValue}
        onChange={onChange}
        onClick={(event) => {
          if (typeof event.currentTarget.showPicker === 'function') {
            event.currentTarget.showPicker();
          }
        }}
        className="absolute inset-0 z-[1] h-full w-full cursor-pointer opacity-0"
      />

      <div
        aria-hidden="true"
        className="pointer-events-none flex min-h-[2.25rem] w-full min-w-0 items-center whitespace-nowrap border-0 border-b border-dashed border-slate-400 bg-transparent p-1 pr-10 text-xs font-medium leading-tight text-slate-900"
      >
        <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
          {displayDateValue || (
            <span className="text-slate-400 font-normal">DD/MM/YYYY</span>
          )}
        </span>
      </div>

      {/* Clear Button */}
      {displayDateValue && onClear && (
        <button
          type="button"
          onClick={() => onClear(name)}
          title="ရက်စွဲရှင်းလင်းမည်"
          className="absolute right-0 text-slate-400 hover:text-rose-500 text-xs px-1 font-bold z-10 print:hidden bg-white/80 rounded"
        >
          ✕
        </button>
      )}
    </div>
  );
};